import ExpenseComponent from './expense-component/expense-component';
import { isExpenseReadOnly, reportEditLockErrorKey } from '../../utils/report-edit-lock';
import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import MainInfo from './main-info/main-info';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { inject } from 'mobx-react';
import withRouter from '../../components/withRouter';
import useLanguageChange from '../../utils/hooks/useLanguageChange';
import { fetch } from '../../utils/api-wrapper';
import { matterRuleSaveErrorKey } from '../../utils/matter-rule-errors';
import { downloadFile } from '../../utils/file-manager';
import './index.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../components/modal.js';
import { cachedFetch } from '../../utils/cached-api-wrapper';
import { resolvePrincipalUserIdFromPayloadUser } from './utils/contract-expense-json.js';
import { getReviewableExpenseIndex } from './utils/expense-review-list.js';
import Utils from '../../utils/utils';
import CommentsBadge from './comments-panel/comments-badge';
import useExpenseReviewForm from './use-expense-review-form';
import ExpenseReviewAlerts from './expense-review-alerts';

// XPEN-3777: a page that sent the person here (the denied line in Approvals) gets them back.
// Only an in-app approvals path is followed, never an arbitrary URL.
export const safeReturnPath = (search) => {
  const target = new URLSearchParams(search || '').get('return_to') || '';
  return /^\/approvals(\?|$)/.test(target) ? target : null;
};

const ReviewExpense = ({ data, navigate, location }) => {
  useLanguageChange(data.t);
  const { account_id, checksum } = useParams();
  const returnPath = safeReturnPath(location?.search);
  const leavePath = returnPath || `/expenses_account/${account_id}`;
  const [expenseData, setExpenseData] = useState(null);
  const [pdfBuff, setPdfBuf] = useState(null);
  /**
   * Expense account owner (principal); drives review-mode imputation catalog for adjoints (XPEN-2455).
   * undefined = owner not resolved yet (do not call imputation catalog);
   * null = resolved, no owner id (GET without review_for_user_id);
   * string = resolved, scope catalog to this user.
   */
  const [imputationReviewForUserId, setImputationReviewForUserId] = useState(undefined);
  const [reviewableExpenseIndex, setReviewableExpenseIndex] = useState(0);
  const [showConfirmGoBack, setShowConfirmGoBack] = useState(false);
  const [readOnly, setReadOnly] = useState(false);

  // Memoize default tax code to avoid repeated calculations
  const defaultTaxCode = useMemo(
    () => data.config?.tax_profile?.find(t => t.is_default === true)?.code ?? "N_IN",
    [data.config?.tax_profile]
  );

  const {
    expense,
    handleUpdateExpense,
    mainInfo,
    setMainInfo,
    validationErrors,
    setValidationErrors,
    ruleAlerts,
    handleExpenseValidationChange,
    handleImputationLeafStageChange,
    handleImputationRowsChange,
    prepareSubmission,
  } = useExpenseReviewForm({ data, expenseData, defaultTaxCode });

  useLayoutEffect(() => {
    setImputationReviewForUserId(undefined);
    setReviewableExpenseIndex(0);
    // Reset per-expense errors on navigation so a previous expense's error is not
    // pinned by the receipt-fetch non-clobber guard when paging to another one.
    setValidationErrors([]);
  }, [account_id, checksum]);

  /** Same source as invoice Expense editor: account document owner is authoritative for review_for_user_id. */
  useEffect(() => {
    if (!account_id) {
      return;
    }
    const searchData = `?account_id=${encodeURIComponent(account_id)}`;
    fetch(`/expenses_account/${searchData}`, 'GET', {}, (response) => {
      const account = response?.data?.expenses_account;
      const ownerId = resolvePrincipalUserIdFromPayloadUser(account?.user);
      setImputationReviewForUserId((prev) => (ownerId != null ? ownerId : prev));
      if (account?.expenses && checksum) {
        setReviewableExpenseIndex(getReviewableExpenseIndex(account.expenses, checksum));
        setReadOnly(isExpenseReadOnly(account, account.expenses.find((e) => e.checksum === checksum)));
      }
    }, () => {
      /* expense GET may still supply user; leave undefined so expense callback can fill */
    });
  }, [account_id, checksum]);

  useEffect(() => {
    if (!account_id || !checksum) {
      return;
    }
    fetch(`/expenses/${account_id}/${checksum}`, 'GET', {}, (response) => {
      setExpenseData(response?.data?.expense);
      const ownerFromExpense = resolvePrincipalUserIdFromPayloadUser(response?.data?.user);
      setImputationReviewForUserId((prev) => {
        if (prev !== undefined && prev !== null) return prev;
        return ownerFromExpense ?? prev ?? null;
      });
    }, (error) => {
      setValidationErrors([data.t.read('review_expense.error.fetch_expense_failed')]);
      setImputationReviewForUserId((prev) => (prev === undefined ? null : prev));
    });
  }, [account_id, checksum]);

  useEffect(() => {
    if (!account_id || !checksum) {
      return;
    }
    let cancelled = false;
    const source = axios.CancelToken.source();
    // Clear the previous receipt while the new one loads; cancel/ignore a response
    // that resolves after the user paged to another receipt (mirrors expense.js).
    setPdfBuf(null);
    // XPEN-3397: resolve the receipt by its stored name (account_id + checksum),
    // like the expense modal and list — not by the extracted document name, which
    // does not match the storage object.
    const pdfFileName = `${encodeURIComponent(account_id)}_${encodeURIComponent(checksum)}.pdf`;
    cachedFetch(`/invoice/pdf/${pdfFileName}`, 'GET', {}, (response) => {
      if (cancelled) return;
      const url = response?.data?.url;
      if (url) {
        setPdfBuf(url);
      }
    }, () => {
      if (cancelled) return;
      // Do not clobber an existing error (e.g. the expense itself failing to load).
      setValidationErrors((prev) =>
        prev.length ? prev : [data.t.read('review_expense.error.fetch_invoice_pdf_failed')]
      );
    }, false, source.token, {
      cacheType: 'pdf',
    });
    return () => {
      cancelled = true;
      source.cancel('receipt fetch superseded');
    };
  }, [account_id, checksum]);



  const submitForm = useCallback(async (e) => {
    e.preventDefault();

    const payload = await prepareSubmission();
    if (!payload) {
      return;
    }

    fetch(
      `/expenses/${account_id}/${checksum}?view=review`,
      'PUT',
      payload,
      (response) => {
        if (response?.data) {
          navigate(leavePath);
        } else {
          setValidationErrors([data.t.read('review_expense.error.update_failed')]);
        }
      },
      (error) => {
        setValidationErrors([data.t.read(reportEditLockErrorKey(error) ?? matterRuleSaveErrorKey(error, 'review_expense.error.update_failed'))]);
      }
    );
  }, [prepareSubmission, account_id, checksum, navigate, data, setValidationErrors, leavePath]);

  return (
    <>
      {showConfirmGoBack && (
        <Modal
          title={data.t.read('confirm')}
          cancelAction={() => setShowConfirmGoBack(false)}
          okAction={() => navigate(leavePath)}
          okText={data.t.read('yes')}
          cancelText={data.t.read('cancel')}
        >
          {data.t.read('go_back_conrifmation')}
        </Modal>
      )}
      <div className='p-5 my-5 rounded card'>
        <div className='d-flex flex-column align-items-start mb-4'>
          <h1 className='mb-4'>{data.t.read('review_expense.title')}</h1>
          <div className='d-flex align-items-center mb-3'>
            <button className='btn btn-light mr-2' onClick={() => setShowConfirmGoBack(true)} type='button'>
              <FontAwesomeIcon icon={faArrowLeft} className='mr-2' />
              {data.t.read('back')}
            </button>
            <CommentsBadge expense={expenseData} comments={expenseData?.json?.comments} />
          </div>
        </div>
        <div className='invoice-review-container'>
          <div className='invoice-review'>
            {pdfBuff && (
              <object width='100%' height='90%' type='application/pdf' data={pdfBuff} aria-label='PDF document'>
                <div>
                  Your browser does not support PDFs. Please download the PDF to view it:
                  <button className='btn btn-link' onClick={() => downloadFile(account_id, 'pdf')} aria-label='Download PDF'>
                    {data.t.read('download_pdf')}
                  </button>
                </div>
              </object>
            )}
          </div>
          <form className='form-invoice-review col-7 ml-1' noValidate onSubmit={(e) => e.preventDefault()}>
            <MainInfo
              mainInfo={mainInfo}
              setMainInfo={setMainInfo}
              showInvoiceNumber={Utils.shouldShowExpenseReviewInvoiceNumber(data)}
            />
            <ExpenseComponent
              expenseIndex={reviewableExpenseIndex}
              expense={expense}
              updateInfo={handleUpdateExpense}
              onValidationChange={handleExpenseValidationChange}
              onImputationLeafStageChange={handleImputationLeafStageChange}
              onImputationRowsChange={handleImputationRowsChange}
              imputationReviewForUserId={imputationReviewForUserId}
              data-testid='expense-component'
            />
            {readOnly && (
              <div className='alert alert-info' role='status' data-testid='review-read-only'>
                {data.t.read('report_edit_lock.read_only')}
              </div>
            )}
            <ExpenseReviewAlerts validationErrors={validationErrors} ruleAlerts={ruleAlerts} />
            <div className='d-flex justify-content-end'>
              <button
                className='btn btn-primary mt-3 col-3'
                onClick={submitForm}
                disabled={readOnly}
                data-testid='save-modifications'
              >
                {data.t.read('review_expense.save_modifications')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default inject('data')(withRouter(ReviewExpense));
